"use client";

import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { ScrollArea } from "@ui/components/scroll-area";
import { cn } from "@ui/lib";
import { format } from "date-fns";
import { CopyIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocalStorage } from "../hooks/use-local-storage";

interface PromptHistoryDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function PromptHistoryDialog({
	open,
	onOpenChange,
}: PromptHistoryDialogProps) {
	const { prompts, loading, clearAll } = useLocalStorage();
	const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(
		new Set(),
	);

	// 处理复制提示词
	const handleCopyPrompt = (prompt: string) => {
		navigator.clipboard.writeText(prompt);
		toast.success("Prompt copied to clipboard");
	};

	// 处理清空历史记录
	const handleClearAll = async () => {
		try {
			await clearAll();
			toast.success("History cleared");
		} catch (error) {
			toast.error("Failed to clear history");
		}
	};

	// 切换提示词展开状态
	const toggleExpand = (id: string) => {
		const newExpanded = new Set(expandedPrompts);
		if (newExpanded.has(id)) {
			newExpanded.delete(id);
		} else {
			newExpanded.add(id);
		}
		setExpandedPrompts(newExpanded);
	};

	// 获取模型显示名称
	const getModelDisplayName = (modelType: string) => {
		const modelNames: Record<string, string> = {
			general: "General",
			flux: "Flux",
			midjourney: "Midjourney",
			"stable-diffusion": "Stable Diffusion",
		};
		return modelNames[modelType] || modelType;
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl h-[80vh] flex flex-col">
				<DialogHeader>
					<div className="flex items-center justify-between">
						<DialogTitle className="text-2xl font-semibold">
							Prompt History
						</DialogTitle>
						<div className="flex items-center gap-2">
							<span className="text-sm text-muted-foreground">
								Maximum 20 items stored locally
							</span>
							<Button
								variant="ghost"
								className="text-purple-500 hover:text-purple-600 hover:bg-purple-50"
								onClick={handleClearAll}
							>
								Clear All
							</Button>
						</div>
					</div>
				</DialogHeader>

				<ScrollArea className="flex-1 pr-4">
					<div className="space-y-4">
						{loading ? (
							<div className="flex items-center justify-center h-32">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
							</div>
						) : prompts.length === 0 ? (
							<div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
								<p>No prompts in history</p>
							</div>
						) : (
							prompts.map((prompt) => (
								<div
									key={prompt.id}
									className="rounded-lg border bg-card text-card-foreground shadow-sm"
								>
									<div className="p-6 space-y-4">
										{/* 时间和模型信息 */}
										<div className="flex items-center justify-between">
											<div className="text-sm text-muted-foreground">
												{format(
													prompt.createdAt,
													"MM/dd/yyyy, HH:mm:ss",
												)}
											</div>
											<Badge
												variant="secondary"
												className="font-medium"
											>
												{getModelDisplayName(
													prompt.modelType,
												)}
											</Badge>
										</div>

										{/* 图片和提示词 */}
										<div className="grid grid-cols-[240px,1fr] gap-6">
											<div className="relative aspect-square rounded-md overflow-hidden bg-muted">
												<img
													src={`data:image/jpeg;base64,${prompt.image}`}
													alt="Generated prompt"
													className="object-cover"
												/>
											</div>
											<div className="space-y-2">
												<div className="relative">
													<p
														className={cn(
															"text-sm leading-relaxed",
															!expandedPrompts.has(
																prompt.id,
															) && "line-clamp-4",
														)}
													>
														{prompt.prompt}
													</p>
													{prompt.prompt.length >
														200 && (
														<Button
															variant="ghost"
															size="sm"
															className="absolute -bottom-6 left-0 text-purple-500 hover:text-purple-600"
															onClick={() =>
																toggleExpand(
																	prompt.id,
																)
															}
														>
															{expandedPrompts.has(
																prompt.id,
															)
																? "Collapse"
																: "Expand"}
														</Button>
													)}
												</div>
											</div>
										</div>

										{/* 操作按钮 */}
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													handleCopyPrompt(
														prompt.prompt,
													)
												}
												className="text-muted-foreground hover:text-foreground"
											>
												<CopyIcon className="h-4 w-4 mr-1" />
												Copy
											</Button>
										</div>
									</div>
								</div>
							))
						)}
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
}
